import { Avaliation } from "../types";

export function parseNumber(value: string | number | undefined): number | null {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  const normalized = value.replace(",", ".");
  const num = Number(normalized);
  if (Number.isNaN(num)) return null;
  return num;
}

export function calculateSumSkinfoldsSeven(
  avaliation: Avaliation
): number | null {
  const folds = (avaliation.dobra_cutanea ?? {}) as Partial<
    Avaliation["dobra_cutanea"]
  >;

  const t = parseNumber(folds.tricipal);
  const se = parseNumber(folds.subescapular);
  const am = parseNumber(folds.axilar_media);
  const peitoral = parseNumber(folds.biceps);
  const si = parseNumber(folds.suprailiaca);
  const coxa = parseNumber(folds.coxa);
  const abd = parseNumber(folds.abdominal);

  if (
    t === null ||
    se === null ||
    am === null ||
    peitoral === null ||
    si === null ||
    coxa === null ||
    abd === null
  ) {
    return null;
  }

  return t + se + am + peitoral + si + coxa + abd;
}

export function calculatePercentualGordura(
  avaliation: Avaliation,
  gender: "M" | "F"
): number | null {
  const s7 = calculateSumSkinfoldsSeven(avaliation);
  const idade = parseNumber(avaliation.idade);

  if (s7 === null || idade === null) return null;

  const base = 1.112;
  if (gender === "M") {
    const bodyDensity =
      base - 0.00043499 * s7 + 0.00000055 * s7 * s7 - 0.00028826 * idade;
    return 495 / bodyDensity - 450;
  }

  const bodyDensity =
    base - 0.00043499 * s7 + 0.00000056 * s7 * s7 - 0.00012828 * idade;
  return 495 / bodyDensity - 450;
}

export function calculateIMC(avaliation: Avaliation): number | null {
  const peso = parseNumber(avaliation.peso);
  let altura = parseNumber(avaliation.altura);

  if (peso === null || altura === null) return null;

  if (altura > 3) {
    altura = altura / 100;
  }

  if (!altura || altura <= 0) return null;

  return peso / (altura * altura);
}

export function calculatePesoMagro(
  avaliation: Avaliation,
  percentualGordura: number | null
): number | null {
  const peso = parseNumber(avaliation.peso);
  if (peso === null || percentualGordura === null) return null;
  return peso - peso * (percentualGordura / 100);
}

export function calculatePesoGordo(
  avaliation: Avaliation,
  pesoMagro: number | null
): number | null {
  const peso = parseNumber(avaliation.peso);
  if (peso === null || pesoMagro === null) return null;
  return peso - pesoMagro;
}

export function calculateSomatorioDobrasNove(
  avaliation: Avaliation
): number | null {
  const folds = (avaliation.dobra_cutanea ?? {}) as Partial<
    Avaliation["dobra_cutanea"]
  >;

  const peitoral = parseNumber(folds.biceps);
  const tricipital = parseNumber(folds.tricipal);
  const subescapular = parseNumber(folds.subescapular);
  const coxa = parseNumber(folds.coxa);
  const abdominal = parseNumber(folds.abdominal);
  const panturrilha = parseNumber(folds.panturrilha);
  const biceps = parseNumber(folds.biceps);
  const axilarMedia = parseNumber(folds.axilar_media);
  const supraIliaca = parseNumber(folds.suprailiaca);

  const values = [
    peitoral,
    tricipital,
    subescapular,
    coxa,
    abdominal,
    panturrilha,
    biceps,
    axilarMedia,
    supraIliaca,
  ];

  if (values.some((v) => v === null)) return null;
  const numericValues = values as number[];
  return numericValues.reduce((total, v) => total + v, 0);
}

export function calculateDistribuicaoDobras(avaliation: Avaliation): {
  distribuicaoMMSS: number | null;
  distribuicaoMMII: number | null;
  distribuicaoTronco: number | null;
} {
  const folds = (avaliation.dobra_cutanea ?? {}) as Partial<
    Avaliation["dobra_cutanea"]
  >;

  const tricipital = parseNumber(folds.tricipal);
  const biceps = parseNumber(folds.biceps);
  const coxa = parseNumber(folds.coxa);
  const subescapular = parseNumber(folds.subescapular);
  const supraIliaca = parseNumber(folds.suprailiaca);
  const peitoral = parseNumber(folds.biceps);
  const abdominal = parseNumber(folds.abdominal);
  const axilarMedia = parseNumber(folds.axilar_media);

  const somatorioNove = calculateSomatorioDobrasNove(avaliation);
  if (
    somatorioNove === null ||
    tricipital === null ||
    biceps === null ||
    coxa === null ||
    subescapular === null ||
    supraIliaca === null ||
    peitoral === null ||
    abdominal === null ||
    axilarMedia === null
  ) {
    return {
      distribuicaoMMSS: null,
      distribuicaoMMII: null,
      distribuicaoTronco: null,
    };
  }

  const mediaGeral = somatorioNove / 9;
  if (!mediaGeral) {
    return {
      distribuicaoMMSS: null,
      distribuicaoMMII: null,
      distribuicaoTronco: null,
    };
  }

  const vmpMMSS = (((tricipital + biceps) / 2) * 100) / (mediaGeral * 9);
  const vmpMMII = (coxa * 100) / (mediaGeral * 9);
  const vmpTronco =
    (((subescapular + supraIliaca + peitoral + abdominal + axilarMedia) / 5) *
      100) /
    (mediaGeral * 9);

  const somaVmp = vmpMMSS + vmpMMII + vmpTronco;
  if (!somaVmp) {
    return {
      distribuicaoMMSS: null,
      distribuicaoMMII: null,
      distribuicaoTronco: null,
    };
  }

  return {
    distribuicaoMMSS: (vmpMMSS * 100) / somaVmp,
    distribuicaoMMII: (vmpMMII * 100) / somaVmp,
    distribuicaoTronco: (vmpTronco * 100) / somaVmp,
  };
}

export function calculateSomatorioPerimetros(avaliation: Avaliation): number | null {
  const values = [
    avaliation.cintura,
    avaliation.abdomen,
    avaliation.quadril,
    avaliation.torax,
    avaliation.braco_direito,
    avaliation.braco_direito_contraido,
    avaliation.braco_esquerdo,
    avaliation.braco_esquerdo_contraido,
    avaliation.coxa_direita,
    avaliation.coxa_esquerda,
    avaliation.perna_direita,
    avaliation.perna_esquerda,
  ].map(parseNumber);

  const numeric = values.filter((v) => v !== null) as number[];
  if (numeric.length === 0) return null;
  return numeric.reduce((sum, v) => sum + v, 0);
}
